import {
  CompetitionPrice,
  CompetitionWithStats,
} from "../../../types/competition.types";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Trophy, FileText } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { UserRoles } from "../../../types/user.types";
import { Button } from "../../../components/button";
import { useState } from "react";
import { CompetitionPricesAPI } from "../../../APIs/competition_prices.api";
import { ConfirmationModal } from "../../../components/delete-confirmation-modal";
import { useToast } from "../../../components/toast";
import {
  PaymentType,
  PaymentTypeTranslations,
} from "../../../types/payments.types";
import { PdfExportModal } from "./competitions-pdf-modal";
import { TeamsAPI } from "../../../APIs/teams.api";

type CompetitionStatsProps = {
  competition: CompetitionWithStats;
};

enum CompetitionStatus {
  NOT_STARTED = "NOT_STARTED",
  REGISTRATION_OPEN = "REGISTRATION_OPEN",
  REGISTRATION_CLOSED = "REGISTRATION_CLOSED",
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
}

type StatusConfig = {
  label: string;
  color: string;
};

const STATUS_CONFIGS: Record<CompetitionStatus, StatusConfig> = {
  [CompetitionStatus.NOT_STARTED]: {
    label: "Próximamente",
    color: "bg-purple-500",
  },
  [CompetitionStatus.REGISTRATION_OPEN]: {
    label: "Inscripciones abiertas",
    color: "bg-green-500",
  },
  [CompetitionStatus.REGISTRATION_CLOSED]: {
    label: "Inscripciones cerradas",
    color: "bg-yellow-500",
  },
  [CompetitionStatus.IN_PROGRESS]: {
    label: "Competencia en curso",
    color: "bg-blue-500",
  },
  [CompetitionStatus.FINISHED]: {
    label: "Competencia finalizada",
    color: "bg-gray-500",
  },
};

export function CompetitionStats({ competition }: CompetitionStatsProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSystemAdmin = user?.roleId === UserRoles.SYSTEM_ADMIN;
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    row: CompetitionPrice | null;
  }>({ isOpen: false, row: null });

  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const { addToast } = useToast();

  const formatCurrency = (value: number | string) => {
    return `$${Number(value).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleDelete = (row: CompetitionPrice) => {
    setDeleteModalState({ isOpen: true, row });
  };

  const handleConfirmDelete = async () => {
    if (deleteModalState.row) {
      try {
        const response = await CompetitionPricesAPI.deleteCompetitionPrice(
          deleteModalState.row.id
        );
        if (response.statusCode === 200) {
          addToast({
            type: "success",
            message: "Precio eliminado correctamente",
            location: "bottom-center",
          });
          window.location.reload();
        } else {
          addToast({
            type: "error",
            message: response.message,
            location: "bottom-center",
          });
        }
      } catch (error) {
        addToast({
          type: "error",
          message: "Error al eliminar el precio",
          location: "bottom-center",
        });
      }

      setDeleteModalState({ isOpen: false, row: null });
    }
  };

  const handlePdfExport = async (status: string) => {
    try {
      const response = await TeamsAPI.exportTeamsPdf(competition.id, status);

      if (response.statusCode === 200 && response.data) {
        const blobUrl = URL.createObjectURL(response.data);

        const today = new Date();
        const dateStr =
          today.getFullYear() +
          "-" +
          String(today.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(today.getDate()).padStart(2, "0");

        const link = document.createElement("a");
        link.href = blobUrl;

        switch (status) {
          case "accepted":
            link.download = `equipos-confirmados-${dateStr}.pdf`;
            break;
          case "registered":
            link.download = `equipos-registrados-${dateStr}.pdf`;
            break;
          case "all":
            link.download = `equipos-${dateStr}.pdf`;
            break;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);

        addToast({
          type: "success",
          message: "PDF descargado",
          location: "bottom-center",
        });
      } else {
        addToast({
          type: "error",
          message: response.message || "Error al generar el PDF",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al generar el PDF",
        location: "bottom-center",
      });
    }
  };

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        <ConfirmationModal
          isOpen={deleteModalState.isOpen}
          onClose={() => setDeleteModalState({ isOpen: false, row: null })}
          onConfirm={handleConfirmDelete}
          title={"Confirmar eliminación"}
          message={"¿Estás seguro de que deseas eliminar este precio?"}
          confirmLabel={"Eliminar"}
          cancelLabel={"Cancelar"}
        />

        {/* Header */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-3">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                  {competition.name}
                  <span className="block sm:inline sm:ml-2 text-base sm:text-lg text-gray-500">
                    Edición {competition.edition}
                  </span>
                </h1>
                {competition.description && (
                  <div className="mt-2 sm:mt-3">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {competition.description}
                    </p>
                  </div>
                )}
              </div>
              <StatusBadge
                registrationStart={competition.registration_start}
                registrationEnd={competition.registration_end}
                competitionStart={competition.start_date}
                competitionEnd={competition.end_date}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() =>
                  navigate("/dashboard/competitions/edit/" + competition.id)
                }
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Editar competencia
              </button>

              <button
                onClick={() =>
                  navigate(
                    "/dashboard/competitions/" + competition.id + "/results"
                  )
                }
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Administrar resultados
              </button>

              <button
                onClick={() => setPdfModalOpen(true)}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 w-full sm:w-auto"
                disabled={competition.total_teams === 0}
              >
                <FileText className="h-4 w-4 mr-2" />
                Exportar PDF de Equipos
              </button>

              {isSystemAdmin && (
                <button
                  onClick={() => navigate("/dashboard/competitions/new")}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Crear nueva competencia
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total de equipos" value={competition.total_teams} />
          <StatCard
            label="Equipos registrados"
            value={competition.registered_teams}
          />
          <StatCard label="Equipos pagos" value={competition.accepted_teams} />
          <StatCard
            label="Total pagado"
            value={formatCurrency(competition.total_paid_amount)}
          />
        </div>

        {/* Important Dates */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 space-y-4 sm:space-y-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Fechas importantes
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <DateRange
              label="Duración de la competencia"
              start={competition.start_date}
              end={competition.end_date}
            />
            <DateRange
              label="Periodo de inscripción"
              start={competition.registration_start}
              end={competition.registration_end}
            />
          </div>
        </div>

        {/* Payment Options */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Opciones de pago
            </h2>

            {isSystemAdmin && (
              <Button
                type="button"
                text="Agregar precio"
                onClick={() => navigate("/dashboard/price/new")}
                variant="create"
                className="w-full sm:w-auto"
              />
            )}
          </div>

          {competition.prices && competition.prices.length > 0 ? (
            <div className="space-y-4">
              {competition.prices.map((priceOption, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 space-y-3"
                >
                  {/* Price Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <span className="text-sm sm:text-base text-gray-600">
                        {formatDate(priceOption.start_date)} -{" "}
                        {formatDate(priceOption.end_date)}
                      </span>

                      <span
                        className={`
                        inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit
                        ${
                          priceOption.payment_type === PaymentType.MERCADOPAGO
                            ? "bg-blue-100 text-blue-800"
                            : priceOption.payment_type === PaymentType.TRANSFER
                            ? "bg-orange-100 text-orange-800"
                            : "bg-green-100 text-green-800"
                        }
                      `}
                      >
                        {
                          PaymentTypeTranslations[
                            priceOption.payment_type as PaymentType
                          ]
                        }
                      </span>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <span className="text-lg sm:text-xl font-bold text-gray-900">
                        {formatCurrency(priceOption.price)}
                      </span>

                      {isSystemAdmin && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            text=""
                            onClick={() =>
                              navigate(
                                `/dashboard/price/edit/${priceOption.id}`
                              )
                            }
                            variant="edit"
                            className="flex-shrink-0"
                          />
                          <Button
                            type="button"
                            text=""
                            onClick={() => handleDelete(priceOption)}
                            variant="delete"
                            className="flex-shrink-0"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price Details */}
                  <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-500">
                    {priceOption.installment_count > 1 && (
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md">
                        {priceOption.installment_count} cuotas
                      </span>
                    )}

                    {priceOption.platform_fee > 0 && (
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md">
                        Comisión: {priceOption.platform_fee}%
                      </span>
                    )}

                    {priceOption.platform_fee === 0 && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md">
                        Sin comisión
                      </span>
                    )}
                  </div>

                  {/* Commission Details */}
                  {priceOption.platform_fee > 0 ? (
                    priceOption.covers_platform_fee ? (
                      <div className="text-xs sm:text-sm bg-blue-50 p-3 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium w-fit">
                            {
                              PaymentTypeTranslations[
                                priceOption.payment_type as PaymentType
                              ]
                            }
                          </span>
                          <span className="text-blue-700 font-medium">
                            Comisión incluida en el precio (
                            {priceOption.platform_fee}%)
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">
                          Comisión de plataforma:{" "}
                          <span className="font-medium">
                            {formatCurrency(
                              (priceOption.price * priceOption.platform_fee) /
                                100
                            )}
                          </span>
                        </p>
                        <p className="text-gray-900 font-medium">
                          Total a recibir:{" "}
                          <span className="text-green-600">
                            {formatCurrency(
                              priceOption.price -
                                (priceOption.price * priceOption.platform_fee) /
                                  100
                            )}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <div className="text-xs sm:text-sm bg-orange-50 p-3 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium w-fit">
                            {
                              PaymentTypeTranslations[
                                priceOption.payment_type as PaymentType
                              ]
                            }
                          </span>
                          <span className="text-orange-700 font-medium">
                            Comisión agregada al precio (
                            {priceOption.platform_fee}%)
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">
                          Comisión de plataforma:{" "}
                          <span className="font-medium">
                            {formatCurrency(
                              (priceOption.price * priceOption.platform_fee) /
                                100
                            )}
                          </span>
                        </p>
                        <p className="text-gray-900 font-medium">
                          Precio total con comisión para participantes:{" "}
                          <span className="text-red-600">
                            {formatCurrency(
                              priceOption.price +
                                (priceOption.price * priceOption.platform_fee) /
                                  100
                            )}
                          </span>
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="text-xs sm:text-sm bg-green-50 p-3 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium w-fit">
                          {
                            PaymentTypeTranslations[
                              priceOption.payment_type as PaymentType
                            ]
                          }
                        </span>
                        <span className="text-green-700 font-medium">
                          Sin comisiones adicionales
                        </span>
                      </div>
                      <p className="text-gray-900 font-medium mt-1">
                        Precio final para participantes:{" "}
                        <span className="text-green-600">
                          {formatCurrency(priceOption.price)}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              No hay opciones de pago configuradas
            </div>
          )}
        </div>
      </div>
      <PdfExportModal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onExport={handlePdfExport}
      />
    </>
  );
}

const getCompetitionStatus = (
  registrationStart: string | Date,
  registrationEnd: string | Date,
  competitionStart: string | Date,
  competitionEnd: string | Date
): CompetitionStatus => {
  const now = new Date();
  const regStart = new Date(registrationStart);
  const regEnd = new Date(registrationEnd);
  const compStart = new Date(competitionStart);
  const compEnd = new Date(competitionEnd);

  if (now < regStart) {
    return CompetitionStatus.NOT_STARTED;
  } else if (now >= regStart && now <= regEnd) {
    return CompetitionStatus.REGISTRATION_OPEN;
  } else if (now > regEnd && now < compStart) {
    return CompetitionStatus.REGISTRATION_CLOSED;
  } else if (now >= compStart && now <= compEnd) {
    return CompetitionStatus.IN_PROGRESS;
  } else {
    return CompetitionStatus.FINISHED;
  }
};

const StatusBadge = ({
  registrationStart,
  registrationEnd,
  competitionStart,
  competitionEnd,
}: {
  registrationStart: string | Date;
  registrationEnd: string | Date;
  competitionStart: string | Date;
  competitionEnd: string | Date;
}) => {
  const status = getCompetitionStatus(
    registrationStart,
    registrationEnd,
    competitionStart,
    competitionEnd
  );
  const config = STATUS_CONFIGS[status];

  return (
    <div className="flex items-center gap-2">
      <div className={`h-2 w-2 rounded-full ${config.color} flex-shrink-0`} />
      <span className="text-xs sm:text-sm font-medium">{config.label}</span>
    </div>
  );
};

interface DateRangeProps {
  label: string;
  start: string | Date;
  end: string | Date;
}

export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date);

  if (isNaN(d.getTime())) {
    return "Fecha inválida";
  }

  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year} - ${hours}:${minutes} hs.`;
};

export const formatDate = (date: string | Date): string => {
  const d = new Date(date);

  if (isNaN(d.getTime())) {
    return "Fecha inválida";
  }

  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

export const DateRange = ({ label, start, end }: DateRangeProps) => {
  return (
    <div className="flex flex-col space-y-1">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm space-y-1 sm:space-y-0 sm:space-x-1">
        <div className="flex items-center">
          <span className="text-black mr-1">Desde</span>
          <time
            className="text-gray-700 tabular-nums"
            dateTime={new Date(start).toISOString()}
          >
            {formatDateTime(start)}
          </time>
        </div>
        <span className="text-black hidden sm:inline">hasta</span>
        <span className="text-black sm:hidden">Hasta</span>
        <time
          className="text-gray-700 tabular-nums"
          dateTime={new Date(end).toISOString()}
        >
          {formatDateTime(end)}
        </time>
      </div>
    </div>
  );
};

const StatCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
    <dt className="text-xs sm:text-sm font-medium text-gray-500 leading-tight">
      {label}
    </dt>
    <dd className="mt-1 text-lg sm:text-2xl font-semibold text-gray-900 truncate">
      {value}
    </dd>
  </div>
);
